import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { LtiParams } from '@mhe/reader/models';
import { ApiClientSelectorService } from '../core/api-client-selector.service';
import { catchError, filter, take, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class LtiService {
  constructor(
    private readonly apiClientSelector: ApiClientSelectorService,
  ) { }

  get ltiParameters$(): Observable<LtiParams> {
    // some integrators set LTI params in the URL
    const directLaunchParams = this.loadDirectLaunchParams();
    if (directLaunchParams !== undefined) {
      return of(directLaunchParams);
    }

    // must wait until the playerApiMigrationFactory has run (async APP_INITIALIZER)
    return this.apiClientSelector.apiFlagChecked.pipe(
      filter(flag => flag !== null),
      take(1),
      switchMap(() => {
        // EPR-15230 Supporting Direct Links
        //
        // This code can be removed after all integrators have mirgated to launch on Reader API and no further calls are made to Player API.
        //
        // Problem, User A launches Book 1 and gets readerapi=true to indicate their session is with Reader API
        // Then, User A copies the URL and sends it to User B
        // When, User B launches Book 1, uses the URL, then the flag to indicate Reader API is absent
        // Outcome, for User B the /lti call goes to Player API instead, and fails
        //
        // Solution, attempt to fetch LTI params based on the rules of the client selector service
        // If that fetch fails, switch to using Reader API (because when the flag is absent, the selector service defaulted to Player API)
        //
        // This solution preserves Open Learning launches via Player API (which is the default and will succeed).
        // This solution preserves Connect and other integrators who are launching via Reader API, because they'll have the readerapi=true flag.
        return this.apiClientSelector.getApiClient().fetchLTIParams().pipe(
          catchError(() => {
            this.apiClientSelector.setUseReaderApi(true);
            return this.apiClientSelector.getApiClient().fetchLTIParams();
          }),
        );
      }),
    );
  }

  private loadDirectLaunchParams(): LtiParams | undefined {
    if (window.location.hash.lastIndexOf('direct-launch') < 0) {
      return undefined;
    }

    const queryString = window.location.hash.replace('#', '?');
    const urlParams = new URLSearchParams(queryString);
    const ltiParams: LtiParams = {};

    urlParams.forEach(function(value, key) {
      ltiParams[key] = value;
    });

    return ltiParams;
  }
}
